import { useContext, useEffect, useRef, useState } from 'react';
import { ApiError, type Api, type StudioView } from '../api';
import type { T } from '../i18n';
import { PERM, PermsContext, useCan } from '../access';
import { CreateHero } from './CreateHero';

const FIELD_ORDER = [
  'identity.brandName', 'identity.name', 'identity.persona', 'languages.primary', 'direction',
  'mission.goal', 'mission.audience', 'mission.successCondition', 'facts', 'style.tone',
  'compliance.require[recording_notice].instruction', 'compliance.require[ai_disclosure].instruction', 'globalIntents[opt_out].description',
];

const show = (field: string, v: unknown, t: T): string => {
  if (field === 'direction' && typeof v === 'string') return t(`direction.${v}`);
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' && x && 'label' in x ? String((x as { label: unknown }).label) : String(x))).join(', ');
  return v === undefined || v === null ? '' : String(v);
};

export const StudioPage = ({ api, t, onDone, onReady }: { api: Api; t: T; onDone: (agentId?: string) => void; onReady: () => void }) => {
  // The panel's chosen language (the operator talks to the studio in it).
  const lang = document.documentElement.lang || navigator.language || 'en';
  // ADR-071: every studio action needs centeros.agents. App does not open this page without it; this is the guard
  // that also keeps the saved session from being fetched (a refused GET is logged as "denied").
  const allowed = useCan()(PERM.agents);
  // null = App has not heard /me yet (or /me failed): "allowed" above then only means "not known".
  const permsKnown = useContext(PermsContext) !== null;
  const [view, setView] = useState<StudioView>();
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string>();
  const [approved, setApproved] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const run = async (fn: () => Promise<StudioView | void>) => {
    setBusy(true);
    setError(undefined);
    try {
      const v = await fn();
      if (v) setView(v);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const id = new URLSearchParams(location.search).get('session');
    if (!id || !allowed) return;
    // On a direct open or reload this page mounts in the same commit as App's `api`, and a child's effects run
    // before App's own /me request: permissions are not known yet. /me needs no permission (never "denied"), so
    // it is asked first; the session is fetched only when centeros.agents is granted, or when /me cannot say
    // (no permission list, or it failed: today's behaviour). Once known, a later "not allowed" cancels this run.
    let live = true;
    void run(async () => {
      const ok = permsKnown || (await api.me().then((m) => !m.permissions || m.permissions.includes(PERM.agents), () => true));
      return live && ok ? api.getStudio(id) : undefined;
    });
    return () => {
      live = false;
    };
  }, [api, allowed]); // eslint-disable-line react-hooks/exhaustive-deps -- permsKnown turning true must not fetch twice

  useEffect(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), [view?.transcript.length, busy]);

  const start = (templateId?: string) =>
    run(async () => {
      const v = await api.startStudio(templateId, lang);
      history.replaceState(null, '', `?p=studio&session=${v.sessionId}`);
      return v;
    });

  const send = () => {
    const msg = text.trim();
    if (!msg || !view) return;
    setText('');
    setView({ ...view, transcript: [...view.transcript, { role: 'user', content: msg, at: new Date().toISOString() }] });
    void run(() => api.sendStudio(view.sessionId, msg));
  };

  if (!allowed) return null;
  const total = FIELD_ORDER.length;
  const done = view ? FIELD_ORDER.filter((f) => !view.missing.includes(f)).length : 0;

  return (
    <section className="studio">
      <header className="page-head">
        <div>
          <h1>{t('studio.title')}</h1>
          <p className="muted">{t('studio.subtitle')}</p>
        </div>
        {view && <span className="progress">{t('studio.progress', { done, total })}</span>}
      </header>
      {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? t('error.generic') : t(`error.${error}`)}</div>}

      {!view && (
        <div>
          <CreateHero title={t('create.hero.title')} body={t('studio.hero.body')}>
            <button type="button" className="hero-primary" disabled={busy} onClick={() => start()}>✦ {t('studio.hero.start')}</button>
            <button type="button" className="hero-secondary" onClick={onReady}>{t('ready.fromStudio')}</button>
          </CreateHero>
          <h2>{t('studio.templates.title')}</h2>
          <p className="muted">{t('studio.templates.hint')}</p>
          <div className="template-grid">
            <button type="button" className="template-card blank" disabled={busy} onClick={() => start()}>
              <span className="template-icon" aria-hidden>✦</span>
              <strong>{t('studio.templates.blank')}</strong>
              <span className="hint">{t('studio.templates.blank.hint')}</span>
            </button>
            <button type="button" className="template-card" onClick={onReady}>
              <span className="template-icon" aria-hidden>▣</span>
              <strong>{t('ready.fromStudio')}</strong>
              <span className="hint">{t('ready.fromStudio.hint')}</span>
            </button>
          </div>
          {busy && <p className="muted">{t('studio.starting')}</p>}
        </div>
      )}

      {view?.template && view.phase !== 'approved' && (view.template.filled.some((k) => k !== 'business.name') || view.template.unresolved.length > 0) && (
        <div className="banner info small">
          {t('templates.filled', { list: view.template.filled.filter((k) => k !== 'business.name').map((k) => t(`templates.var.${k}`)).join(', ') || '—' })}
          {view.template.unresolved.length > 0 && <> {t('templates.unresolved', { list: view.template.unresolved.map((k) => t(`templates.var.${k}`)).join(', ') })}</>}
        </div>
      )}

      {view && (
        <div className="studio-grid">
          <div className="chat">
            <div className="messages">
              {view.transcript.map((m, i) => (
                <div key={i} className={`bubble ${m.role}`}>{m.content}</div>
              ))}
              {busy && <div className="bubble assistant typing">{t('studio.thinking')}</div>}
              <div ref={endRef} />
            </div>
            {view.phase !== 'approved' && (
              <form className="composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
                <textarea
                  value={text}
                  placeholder={t('studio.placeholder')}
                  rows={2}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                />
                <button type="submit" className="primary" disabled={busy || !text.trim()}>{t('studio.send')}</button>
              </form>
            )}
          </div>

          <aside className="draft">
            <h2>{t('studio.draft')}</h2>
            <div className="bar"><div style={{ width: `${Math.round((done / total) * 100)}%` }} /></div>
            <ul>
              {FIELD_ORDER.map((f) => {
                const missing = view.missing.includes(f);
                // The field the studio's last message asked about.
                const asking = missing && [...view.transcript].reverse().find((m) => m.role === 'assistant')?.askingAbout === f;
                return (
                  <li key={f} className={`${missing ? 'missing' : 'filled'}${asking ? ' asking' : ''}`}>
                    <div className="field-label">
                      <span>{t(`field.${f}`)}</span>
                      <span className={`tag ${missing ? 'warn' : 'ok'}`}>{missing ? (asking ? t('studio.askingNow') : t('studio.missing')) : t('studio.filled')}</span>
                    </div>
                    {!missing && <div className="field-value">{show(f, view.draft[f], t)}</div>}
                    {missing && <div className="field-help">{t(`fieldHelp.${f}`)}</div>}
                  </li>
                );
              })}
            </ul>
            {approved ? (
              <div className="banner ok">{t('studio.approved')}</div>
            ) : view.phase === 'review' ? (
              <button type="button" className="primary wide" disabled={busy} onClick={() => run(async () => {
                const r = await api.approve(view.sessionId);
                // The business's time zone from this device (it can be changed in the Actions tab).
                await api.saveSpec(r.agentId, { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }).catch(() => undefined);
                setApproved(true);
                setTimeout(() => onDone(r.agentId), 1500);
              })}>
                {t('studio.approve')}
              </button>
            ) : (
              <button type="button" className="secondary wide" disabled={busy || view.missing.length > 0} onClick={() => run(() => api.propose(view.sessionId))}>
                {t('studio.propose')}
              </button>
            )}
          </aside>
        </div>
      )}
    </section>
  );
};
